/** 
 * @namespace Accordion
 * @memberof Promega.Components
 * @description Methods for controlling accordion components.
 * 
 * @example <caption>An accordion component has the class <code>content-container--accordion</code>, and
 * sections within an accordion have the class <code>accordion__content</code>. Accordion sections are
 * only visible if they include the class <code>accordion__content--visible</code>; otherwise, they
 * are considered collapsed and are hidden from view.</caption>
 * <div class="content-container--accordion">
 *   <div class="accordion__intro">This is a section of the accordion. It is always visible since it is the intro section.</div>
 *   <div class="accordion__content">This is a second section of the accordion.</div>
 *   <div class="accordion__content">This is a third section of the accordion.</div>
 * </div>
 * 
 * @example <caption>A heading-style button may optionally accompany each accordion section. It may be paired with an accordion section
 * by giving both the same <code>data-section-name</code> attribute.</caption>
 * <div class="content-container--accordion">
 *   <button type="button" class="accordion__button" data-section-name="an-accordion-section">Some text for the header button</button>
 *   <div class="accordion__content accordion__content--visible" data-section-name="an-accordion-section">This is a section of the accordion.</div>
 * </div>
 * 
 * @example <caption>A normal "Read More"/"Read Less" button may be included outside of a section, and paired by giving it the same
 * <code>data-section-name</code> attribute as the section. Use the attributes <code>data-read-more-text</code> and <code>data-read-less-text</code>
 * attributes on the accordion component itself to set the text that is displayed on this button when expanded or collapsed.</caption>
* <div class="content-container--accordion" data-read-more-text="See all" data-read-less-text="See fewer">
 *   <div class="accordion__content accordion__content--visible" data-section-name="an-accordion-section">This is a section of the accordion.</div>
 *   <button type="button" class="button button--light-gray accordion__read-more-less" data-section-name="an-accordion-section">See all</button>
 * </div>
 */

import getQueryStringParameter from '../util/getQueryStringParameter';
import addOrReplaceQueryStringParameter from '../util/addOrReplaceQueryStringParameter';
import removeQueryStringParameter from '../util/removeQueryStringParameter';
import trackEvent from '../util/trackEvent';

// Private functions/listeners
function accordionButtonClicked (event) {
    let eventTarget = event.currentTarget,
        accordionParent = eventTarget.closest(".content-container--accordion");

        if (eventTarget && accordionParent) {
            let sectionName = eventTarget.getAttribute("data-section-name");
            if (sectionName) {
                const eventSource = event.target.classList.contains("accordion__read-more-less") ? "Read more/less button" : "Heading toggle";
                if (isSectionOpen(sectionName, accordionParent)) {
                    // Collapse the section
                    collapseSection(sectionName, accordionParent);
                    trackEvent("Components", "Accordion - collapsed a section", eventSource);
                }
                else {
                    // Expand the section
                    expandSection(sectionName, accordionParent);
                    trackEvent("Components", "Accordion - expanded a section", eventSource);
                }
            }
        }
}

function updateAccordionQueryString () {
    let allAccordionContainers = document.getElementsByClassName("content-container--accordion"),
        containerCount = allAccordionContainers.length;
    for (let i=0; i<containerCount; i++) {
        let currentContainer = allAccordionContainers[i],
            visibleSections = currentContainer.querySelectorAll(".accordion__content--visible"),
            visibleCount = visibleSections.length,
            paramVal = "";

        if (!currentContainer.classList.contains("content-container--accordion-no-query")) {
            // Add the index of each visible section to the parameter value
            for (let k = 0; k < visibleCount; k++) {
                let currentSection = visibleSections[k],
                    sectionName = currentSection.getAttribute("data-section-name");
                paramVal += getIndexOfSection(sectionName, currentContainer);

                // Add a comma if we're not yet on the last visible section
                if (visibleCount - 1 > k) paramVal += ",";
            }

            if (history && history.replaceState) {
                if (paramVal.length > 0) {
                    history.replaceState(null, null, addOrReplaceQueryStringParameter(location, "accordion" + i, paramVal));
                }
                else {
                    history.replaceState(null, null, removeQueryStringParameter(location.href, "accordion" + i));
                }
            }
        }
    }
}
    

// API

/**
 * @function expandSection
 * @memberof Promega.Components.Accordion
 * @description Expands a section in the given accordion.
 * @param {String} sectionName The name of the section to expand
 * @param {HTMLElement} sectionContainer An element representing the entire accordion component
 * @param {JSON} [options]
 * @param {Boolean} options.skipQueryStringUpdate If true, skips updating the browser query string
 */
export function expandSection (sectionName, sectionContainer, options) {
    if (sectionName && sectionContainer) {
        let sectionToExpand = sectionContainer.querySelector(".accordion__content[data-section-name='" + sectionName + "']"),
            buttonToActivate = sectionContainer.querySelector(".accordion__button[data-section-name='" + sectionName + "']"),
            readMoreLessButton = sectionContainer.querySelector(".accordion__read-more-less[data-section-name='" + sectionName + "']"),
            readLessText = sectionContainer.getAttribute("data-read-less-text");
        sectionToExpand.classList.add("accordion__content--visible");
        if (buttonToActivate) buttonToActivate.classList.add("accordion__button--active");
        if (readMoreLessButton && readLessText) readMoreLessButton.innerText = readLessText;
        if (!(options && options.skipQueryStringUpdate)) updateAccordionQueryString();
    }
}

/**
 * @function collapseSection
 * @memberof Promega.Components.Accordion
 * @description Collapses a section in the given accordion.
 * @param {String} sectionName The name of the section to collapse
 * @param {HTMLElement} sectionContainer An element representing the entire accordion component
 * @param {JSON} [options]
 * @param {Boolean} options.skipQueryStringUpdate If true, skips updating the browser query string
 */
export function collapseSection (sectionName, sectionContainer, options) {
    if (sectionName && sectionContainer) {
        let sectionToCollapse = sectionContainer.querySelector(".accordion__content[data-section-name='" + sectionName + "']"),
            buttonToDeactivate = sectionContainer.querySelector(".accordion__button[data-section-name='" + sectionName + "']"),
            readMoreLessButton = sectionContainer.querySelector(".accordion__read-more-less[data-section-name='" + sectionName + "']"),
            readMoreText = sectionContainer.getAttribute("data-read-more-text");
        sectionToCollapse.classList.remove("accordion__content--visible");
        if (buttonToDeactivate) buttonToDeactivate.classList.remove("accordion__button--active");
        if (readMoreLessButton && readMoreText) readMoreLessButton.innerText = readMoreText;
        if (!(options && options.skipQueryStringUpdate)) updateAccordionQueryString();
    }
}

/**
 * @function isSectionOpen
 * @memberof Promega.Components.Accordion
 * @description Returns a value indicating whether or not the given accordion section is currently expanded.
 * @param {String} sectionName The name of the section
 * @param {HTMLElement} sectionContainer An element representing the entire accordion component
 * @returns {Boolean} True if the accordion section is open, otherwise false.
 */
export function isSectionOpen (sectionName, sectionContainer) {
    if (sectionName && sectionContainer) {
        let sectionElement = sectionContainer.querySelector(".accordion__content[data-section-name='" + sectionName + "']");
        if (sectionElement) {
            if (sectionElement.classList.contains("accordion__content--visible")) return true;
            else return false;
        }
        else throw ("Accordion section \"" + sectionName + "\" does not exist.");
    }
}

/**
 * @function getIndexOfSection
 * @memberof Promega.Components.Accordion
 * @description Given the name of a section, returns its index within the accordion component.
 * @param {String} sectionName The name of the section
 * @param {HTMLElement} sectionContainer An element representing the entire accordion component
 * @returns {Number} The zero-based index of the section within the accordion component
 */
export function getIndexOfSection (sectionName, sectionContainer) {
    let sectionElement = sectionContainer.querySelector(".accordion__content[data-section-name='" + sectionName + "']"),
        allSectionsInContainer = sectionContainer.querySelectorAll(".accordion__content"),
        sectionCount = allSectionsInContainer.length,
        sectionIndex = -1;
    for (let i=0; i<sectionCount; i++) {
        if (sectionElement === allSectionsInContainer[i]) {
            sectionIndex = i;
            break;
        }
    }
    return sectionIndex;
}

/**
 * @function getNameOfSection
 * @memberof Promega.Components.Accordion
 * @description Given the name of a section, returns its index within the accordion component.
 * @param {Number} sectionIndex The zero-based index of the section within the accordion component
 * @param {HTMLElement} sectionContainer An element representing the entire accordion component
 * @returns {String} The name of the section
 */
export function getNameOfSection (sectionIndex, sectionContainer) {
    let allSectionsInContainer = sectionContainer.querySelectorAll(".accordion__content"),
        sectionAtIndex = allSectionsInContainer[sectionIndex];
    if (sectionAtIndex) return sectionAtIndex.getAttribute("data-section-name");
}
    

// Init code

{
    const allAccordions = document.getElementsByClassName("content-container--accordion"),
        accordionCount = allAccordions.length;
    for (let i = 0; i < accordionCount; i++) {
        const currentAccordion = allAccordions[i],
            allButtons = currentAccordion.getElementsByClassName("accordion__button"),
            buttonCount = allButtons.length;
        for (let k = 0; k < buttonCount; k++) {
            allButtons[k].addEventListener("click", accordionButtonClicked);
        }

        const readMoreLessButtons = currentAccordion.getElementsByClassName("accordion__read-more-less"),
            readMoreLessCount = readMoreLessButtons.length;
        for (let k = 0; k < readMoreLessCount; k++) {
            readMoreLessButtons[k].addEventListener("click", accordionButtonClicked);
        }

        // Open specific sections based on the query string value (if applicable)
        if (!currentAccordion.classList.contains("content-container--accordion-no-query")) {
            const queryStringVal = getQueryStringParameter(location, "accordion" + i);
            if (queryStringVal && queryStringVal.length > 0) {
                const sectionsToExpand = queryStringVal.split(","),
                    sectionCount = sectionsToExpand.length;
                for (let k = 0; k < sectionCount; k++) {
                    const sectionIndexToOpen = parseInt(sectionsToExpand[k], 10),
                        sectionNameToOpen = getNameOfSection(sectionIndexToOpen, currentAccordion);
                    expandSection(sectionNameToOpen, currentAccordion, { skipQueryStringUpdate: true });
                }
            }
        }
    }
}
